import { getStrategy, listStrategies } from './strategies';
import { paramsFromSchema } from './strategies/params';
import {
  BACKTEST_RANGE_PRESETS,
  defaultRangeForUnit,
  defaultRangePreset,
} from './engine/bars';
import {
  DEFAULT_BACKTEST_QTY,
  DEFAULT_MAX_RISK,
  DEFAULT_POSITION_MODE,
  DEFAULT_RISK_PERCENT,
  DEFAULT_RISK_PERCENT_BASIS,
  DEFAULT_SIZE_MODE,
  DEFAULT_STARTING_BALANCE,
  isPositionMode,
  isRiskPercentBasis,
  isSizeMode,
  parsePositiveNumber,
  qtyFromFixed,
} from './engine/positionMode';
import { DEFAULT_TRADE_SESSION, normalizeTradeSession } from './engine/tradeSession';
import { LAYOUT_STORAGE_KEYS, readLayoutJson, writeLayoutJson } from '../utils/layoutLocalStorage';

export const SESSION_TEMPLATES = ['Default', 'USEQPreAndPost'];
export const DEFAULT_INTERVAL_KEY = '5|Minute';
/** Bump when the recommended sizing default changes so older saved configs pick it up once. */
export const SIZING_DEFAULT_VERSION = 2;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function defaultWorkspaceState(selectedTicker) {
  const strategy = getStrategy('ma-crossover');
  return {
    strategyId: strategy.id,
    ticker: String(selectedTicker || 'MES').toUpperCase(),
    intervalKey: DEFAULT_INTERVAL_KEY,
    rangePreset: defaultRangePreset('Minute'),
    range: defaultRangeForUnit('Minute'),
    sessionTemplate: 'Default',
    params: paramsFromSchema(strategy.paramSchema),
    positionMode: DEFAULT_POSITION_MODE,
    sizeMode: DEFAULT_SIZE_MODE,
    fixedQty: DEFAULT_BACKTEST_QTY,
    maxRisk: DEFAULT_MAX_RISK,
    startingBalance: DEFAULT_STARTING_BALANCE,
    riskPercent: DEFAULT_RISK_PERCENT,
    riskPercentBasis: DEFAULT_RISK_PERCENT_BASIS,
    tradeSession: { ...DEFAULT_TRADE_SESSION },
  };
}

function validIntervalKey(value, allowed) {
  return allowed.includes(value) ? value : DEFAULT_INTERVAL_KEY;
}

export function loadWorkspaceState({
  selectedTicker,
  intervalKeys = [DEFAULT_INTERVAL_KEY],
} = {}) {
  const fallback = defaultWorkspaceState(selectedTicker);
  const raw = readLayoutJson(LAYOUT_STORAGE_KEYS.backtestWorkspace, null);
  if (!raw || typeof raw !== 'object') return fallback;

  const strategyIds = listStrategies().map((s) => s.id);
  const strategyId = strategyIds.includes(raw.strategyId) ? raw.strategyId : fallback.strategyId;
  const strategy = getStrategy(strategyId);
  const ticker = String(raw.ticker || fallback.ticker).trim().toUpperCase() || fallback.ticker;
  const intervalKey = validIntervalKey(raw.intervalKey, intervalKeys);
  const presetIds = BACKTEST_RANGE_PRESETS.map((p) => p.id);
  const rangePreset = presetIds.includes(raw.rangePreset) ? raw.rangePreset : fallback.rangePreset;
  const firstdate = DATE_RE.test(raw.range?.firstdate) ? raw.range.firstdate : fallback.range.firstdate;
  const lastdate = DATE_RE.test(raw.range?.lastdate) ? raw.range.lastdate : fallback.range.lastdate;
  const sessionTemplate = SESSION_TEMPLATES.includes(raw.sessionTemplate)
    ? raw.sessionTemplate
    : fallback.sessionTemplate;

  const positionMode = isPositionMode(raw.positionMode) ? raw.positionMode : fallback.positionMode;
  const adoptSizingDefault = Number(raw.sizingDefaultVersion) !== SIZING_DEFAULT_VERSION;
  const sizeMode = adoptSizingDefault
    ? fallback.sizeMode
    : (isSizeMode(raw.sizeMode) ? raw.sizeMode : fallback.sizeMode);
  const fixedQty = qtyFromFixed(raw.fixedQty ?? fallback.fixedQty);
  const maxRisk = parsePositiveNumber(raw.maxRisk, fallback.maxRisk);
  const startingBalance = parsePositiveNumber(raw.startingBalance, fallback.startingBalance);
  const riskPercent = adoptSizingDefault
    ? fallback.riskPercent
    : parsePositiveNumber(raw.riskPercent, fallback.riskPercent);
  const riskPercentBasis = isRiskPercentBasis(raw.riskPercentBasis)
    ? raw.riskPercentBasis
    : fallback.riskPercentBasis;
  const tradeSession = normalizeTradeSession(
    raw.tradeSession || strategy.defaultTradeSession || fallback.tradeSession,
  );

  return {
    strategyId,
    ticker,
    intervalKey,
    rangePreset,
    range: { firstdate, lastdate },
    sessionTemplate,
    params: paramsFromSchema(strategy.paramSchema, raw.params || {}),
    positionMode,
    sizeMode,
    fixedQty,
    maxRisk,
    startingBalance,
    riskPercent,
    riskPercentBasis,
    tradeSession,
  };
}

export function saveWorkspaceState(state) {
  if (!state) return;
  writeLayoutJson(LAYOUT_STORAGE_KEYS.backtestWorkspace, {
    strategyId: state.strategyId,
    ticker: state.ticker,
    intervalKey: state.intervalKey,
    rangePreset: state.rangePreset,
    range: state.range,
    sessionTemplate: state.sessionTemplate,
    params: state.params,
    positionMode: state.positionMode,
    sizeMode: state.sizeMode,
    fixedQty: state.fixedQty,
    maxRisk: state.maxRisk,
    startingBalance: state.startingBalance,
    riskPercent: state.riskPercent,
    riskPercentBasis: state.riskPercentBasis,
    tradeSession: normalizeTradeSession(state.tradeSession),
    sizingDefaultVersion: SIZING_DEFAULT_VERSION,
  });
}
